import type { Mode, Op } from "../content/modes";
import type { SaveState } from "../save/schema";
import type { Answer } from "./round";

/*
  Play as events. Everything that changes a child's progress is one of these:
  a trial, a practice, a battle, a purchase, a rank change (placement or
  parent), or a baseline imported from an older local save. A child's progress is the fold of their
  events, in the order the server recorded them, so every device agrees.
*/

/** The parts of a save that events build up. */
export type Progress = Pick<
  SaveState,
  | "rank"
  | "trials"
  | "history"
  | "mastery"
  | "missed"
  | "stripes"
  | "sets"
  | "overall"
  | "retuned"
  | "coins"
  | "roster"
  | "spent"
  | "items"
>;

interface EventBase {
  /** Client-made id; recording the same event twice does nothing. */
  id: string;
  /** When it happened, ISO date-time. */
  at: string;
  /** The date on the device when it happened, YYYY-MM-DD. */
  day: string;
}

export interface TrialEvent extends EventBase {
  kind: "trial";
  mode: Mode;
  rankIndex: number;
  score: number;
  tried: number;
  seconds: number;
  passed: boolean;
  audio: boolean;
  seed: number;
  /** Which version of the trial numbers (content/trials.ts) it was played on. */
  trialsVersion: number;
  /** The strands switched on at the time (a Mix trial drew from these). */
  ops?: Op[];
  /** Coins earned: a first pass pays a bonus. Missing on events from before coins. */
  coins?: number;
  durationMs: number | null;
  answers: Answer[];
}

export interface PracticeEvent extends EventBase {
  kind: "practice";
  mode: Mode;
  score: number;
  tried: number;
  /** Time limit; 0 for untimed. */
  seconds: number;
  audio: boolean;
  /** The strands switched on at the time. */
  ops?: Op[];
  /** Coins earned, a trickle. Missing on events from before coins. */
  coins?: number;
  durationMs: number | null;
  answers: Answer[];
}

export type BattleEnd = "won" | "knockout" | "timeout" | "retreat";

export interface BattleEvent extends EventBase {
  kind: "battle";
  mode: Mode;
  /** Ninja ids. */
  enemy: string;
  fighter: string;
  /** The clock, including the fighter's Speed bonus. */
  seconds: number;
  score: number;
  tried: number;
  /** Question points scored; hit points dealt; hits taken. */
  points: number;
  damage: number;
  hits: number;
  won: boolean;
  end: BattleEnd;
  moveUsed: boolean;
  /** The kit fought with, and the active items pressed. Missing on events from before items. */
  items?: { id: string; level: number }[];
  itemsUsed?: string[];
  /** Coins earned, including a worn Lucky Charm's share. */
  coins: number;
  seed: number;
  audio: boolean;
  /** Which version of the battle numbers (content/battles.ts) it was played on. */
  battlesVersion: number;
  ops?: Op[];
  durationMs: number | null;
  answers: Answer[];
}

/**
 * A level of an item bought in the Dojo Shop. The fold applies it only if the
 * level is the next one and the coins cover it, so two devices buying offline
 * end with one level and one price paid; the server checks the same.
 */
export interface PurchaseEvent extends EventBase {
  kind: "purchase";
  item: string;
  level: number;
  /** Coins paid, at the shop version's price. */
  price: number;
  /** Which version of the prices (content/items.ts) it was bought at. */
  shopVersion: number;
}

export interface RankEvent extends EventBase {
  kind: "rank";
  mode: Mode;
  rankIndex: number;
  /**
   * Placement never lowers a rank; a parent can set any rank; "retune" is the
   * one-off v1.1 award for a best that already meets a new, lower pass mark.
   */
  reason: "placement" | "parent" | "retune";
  /** The strands switched on at the time, for gold and foil cards. */
  ops?: Op[];
}

export interface BaselineEvent extends EventBase {
  kind: "baseline";
  progress: Progress;
}

export type PlayEvent =
  TrialEvent | PracticeEvent | BattleEvent | PurchaseEvent | RankEvent | BaselineEvent;

export function emptyProgress(): Progress {
  return {
    rank: { add: 0, sub: 0, mul: 0, div: 0, mix: 0 },
    trials: {},
    history: [],
    mastery: {},
    missed: {},
    stripes: {},
    sets: {},
    overall: 0,
    retuned: [],
    coins: 0,
    roster: {},
    spent: 0,
    items: {},
  };
}

/** Random v4 UUID for event and child ids. */
export function newId(random: () => number = Math.random): string {
  const c = globalThis.crypto;
  if (c?.randomUUID && random === Math.random) return c.randomUUID();
  const hex = Array.from({ length: 32 }, () => Math.floor(random() * 16).toString(16));
  hex[12] = "4";
  hex[16] = ((parseInt(hex[16]!, 16) & 0x3) | 0x8).toString(16);
  const h = hex.join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
